import { t } from '@lingui/macro'
import { Button, Dialog, DialogContent, Tooltip } from '@mui/material'
import { ContactLogResponse } from '@om1/aspen-api/codegen/client/models/ContactLogResponse'
import { EntityType, LogAttemptData, useContactAttempts } from '@om1/aspen-api/codegen/hooks/useContactAttempts'
import { NotebookPen } from 'lucide-react'
import React, { ReactNode, useEffect, useState } from 'react'
import { LogAttemptInput } from './LogAttemptInput'
import { LogAttemptTable } from './LogAttemptTable'

const ActionButton = ({
    icon,
    onClick,
    className = '',
    primary = false,
    disabled = false
}: {
    icon: ReactNode
    onClick: () => void
    className?: string
    primary?: boolean
    disabled?: boolean
}) => {
    const baseClasses = t`flex items-center justify-center py-1.5 rounded-md transition-colors text-sm font-medium min-w-[40px] shadow-sm h-[36px]`
    const primaryClasses = t`bg-primary-main text-white hover:bg-primary-dark`
    const secondaryClasses = t`bg-white border border-gray-200 text-primary-main hover:bg-gray-50`
    const disabledClasses = t`opacity-50 cursor-not-allowed`

    return (
        <Tooltip title={t`Log Contact Attempt`}>
            <button
                onClick={onClick}
                disabled={disabled}
                className={`${baseClasses} ${primary ? primaryClasses : secondaryClasses} ${disabled ? disabledClasses : ''} ${className}`}
            >
                {icon}
            </button>
        </Tooltip>
    )
}

interface LogAttemptActionProps {
    entityName: string
    entityId: string
    entityType: EntityType
    onLogAttempt?: (data: LogAttemptData) => void
}

const LogAttemptAction: React.FC<LogAttemptActionProps> = ({ entityName, entityId, entityType, onLogAttempt }) => {
    const [open, setOpen] = useState(false)
    const [showInputForm, setShowInputForm] = useState(false)
    const [isButtonDisabled, setIsButtonDisabled] = useState(false)
    const [editingAttempt, setEditingAttempt] = useState<ContactLogResponse | null>(null)

    const { contactAttempts, isLoading, error, fetchContactAttempts, submitAttempt, updateAttempt } = useContactAttempts({
        entityType,
        entityId
    })

    useEffect(() => {
        if (open) {
            fetchContactAttempts()
        }
    }, [open, fetchContactAttempts])

    const handleOpen = () => {
        if (isButtonDisabled) return
        setIsButtonDisabled(true)
        setOpen(true)
        // Re-enable the button after a short delay
        setTimeout(() => setIsButtonDisabled(false), 500)
    }

    const handleClose = () => {
        setOpen(false)
        setShowInputForm(false)
        setEditingAttempt(null)
    }

    const handleSubmitAttempt = async (data: LogAttemptData) => {
        await submitAttempt(data)
        if (onLogAttempt) {
            onLogAttempt(data)
        }
        setShowInputForm(false)
    }

    const handleEditAttempt = (attempt: ContactLogResponse) => {
        setEditingAttempt(attempt)
        setShowInputForm(true)
    }

    const handleUpdateAttempt = async (data: LogAttemptData) => {
        if (!editingAttempt) return

        const success = await updateAttempt({
            contactAttemptId: editingAttempt.id,
            contactType: data.contactType,
            outcome: data.outcome,
            notes: data.notes,
            nextContactTime: data.nextContactTime
        })

        if (success) {
            setShowInputForm(false)
            setEditingAttempt(null)
        }
    }

    const getEntityName = () => {
        const entity = entityName
        if (!entity) return t`Unknown`
        return entity
    }

    const getEntityType = (): EntityType | undefined => {
        return entityType
    }

    const getEntityId = () => {
        return entityId
    }

    return (
        <>
            <ActionButton icon={<NotebookPen className='w-4 h-4' />} onClick={handleOpen} disabled={isButtonDisabled} />

            {open && (
                <Dialog
                    open={open}
                    onClose={handleClose}
                    maxWidth='md'
                    fullWidth
                    PaperProps={{
                        sx: {
                            width: '800px',
                            maxHeight: '80vh'
                        }
                    }}
                >
                    <DialogContent sx={{ p: 3 }}>
                        {!showInputForm ? (
                            <div className='space-y-4'>
                                <div className='flex justify-between items-center'>
                                    <h2 className='text-xl font-semibold'>{t`Contact Attempts`}</h2>
                                    <Button color='primary' onClick={() => setShowInputForm(true)} startIcon={<span>{t`+`}</span>}>
                                        {t`Add New Attempt`}
                                    </Button>
                                </div>

                                <LogAttemptTable attempts={contactAttempts} onEdit={handleEditAttempt} />
                            </div>
                        ) : (
                            <div className='flex justify-center items-center min-h-[60vh]'>
                                <div className='w-[500px] mx-auto'>
                                    <LogAttemptInput
                                        entityName={getEntityName()}
                                        onSubmit={editingAttempt ? handleUpdateAttempt : handleSubmitAttempt}
                                        onCancel={() => {
                                            setShowInputForm(false)
                                            setEditingAttempt(null)
                                        }}
                                        entityType={getEntityType()}
                                        entityId={getEntityId()}
                                        contactAttempts={contactAttempts}
                                        isLoading={isLoading}
                                        error={error}
                                        initialValues={
                                            editingAttempt
                                                ? {
                                                      contactType: editingAttempt.contactType,
                                                      outcome: editingAttempt.attemptOutcome,
                                                      notes: editingAttempt.notes,
                                                      nextContactTime: editingAttempt.suggestedNextContactTime
                                                          ? new Date(editingAttempt.suggestedNextContactTime)
                                                          : null
                                                  }
                                                : undefined
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </>
    )
}

export default LogAttemptAction
